'use client';
import { jsx } from 'react/jsx-runtime';
import { usePreventScroll } from '@react-aria/overlays';
import { useState, useRef, useCallback, useEffect } from 'react';
import { removeCoreProps } from '../core/core.utils.js';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { useTransition } from '../../hooks/use-transition.js';
import { ModalDialogContext } from './modal-dialog.context.js';
import { getComponentConfigProvider } from '../../contexts/internal/component-config/component-config.component.js';
import { isFunction } from '../../../core/utilities/is.utils.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';
import { className } from '../../../core/utilities/classnames.utils.js';
import { processStyleProps } from '../../../core/utilities/swan-style.js';
import { RenderComp } from '../core/render-comp.component.js';

function _define_property(e, r, o) {
    return r in e ? Object.defineProperty(e, r, {
        value: o,
        enumerable: true,
        configurable: true,
        writable: true
    }) : e[r] = o, e;
}
function _object_spread(e) {
    for(var r = 1; r < arguments.length; r++){
        var o = null != arguments[r] ? arguments[r] : {}, t = Object.keys(o);
        "function" == typeof Object.getOwnPropertySymbols && (t = t.concat(Object.getOwnPropertySymbols(o).filter(function(e) {
            return Object.getOwnPropertyDescriptor(o, e).enumerable;
        }))), t.forEach(function(r) {
            _define_property(e, r, o[r]);
        });
    }
    return e;
}
function ownKeys(e, r) {
    var o = Object.keys(e);
    if (Object.getOwnPropertySymbols) {
        var t = Object.getOwnPropertySymbols(e);
        o.push.apply(o, t);
    }
    return o;
}
function _object_spread_props(e, r) {
    return r = null != r ? r : {}, Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(r)) : ownKeys(Object(r)).forEach(function(o) {
        Object.defineProperty(e, o, Object.getOwnPropertyDescriptor(r, o));
    }), e;
}
function _object_without_properties(e, r) {
    if (null == e) return {};
    var o, t, n = _object_without_properties_loose(e, r);
    if (Object.getOwnPropertySymbols) {
        var l = Object.getOwnPropertySymbols(e);
        for(t = 0; t < l.length; t++)o = l[t], !(r.indexOf(o) >= 0) && Object.prototype.propertyIsEnumerable.call(e, o) && (n[o] = e[o]);
    }
    return n;
}
function _object_without_properties_loose(e, r) {
    if (null == e) return {};
    var o, t, n = {}, l = Object.keys(e);
    for(t = 0; t < l.length; t++)o = l[t], r.indexOf(o) >= 0 || (n[o] = e[o]);
    return n;
}
let [O, g, h] = getComponentConfigProvider('Dialog');
const Dialog = renderWithRef('Dialog', null, (p, f)=>{
    useComponentStylesLoaded('Dialog', SWAN_STYLE_KEY_MAP.modalDialog);
    let { variant: g = 'standard', onRequestDismiss: h, takeOver: j = false, bodyWidth: w = 'standard', isOpen: _ = false, children: D, isModal: P = false, onlyRenderWhenOpen: M = false, bgc: k, backgroundColor: E } = p, x = _object_without_properties(p, [
        "variant",
        "onRequestDismiss",
        "takeOver",
        "bodyWidth",
        "isOpen",
        "children",
        "isModal",
        "onlyRenderWhenOpen",
        "bgc",
        "backgroundColor"
    ]), [C, L] = useState(!!_), [S, N] = useState(void 0), [R, W] = useState(void 0), I = useTransition(!!_);
    usePreventScroll({
        isDisabled: !C
    });
    let K = useRef(null), q = useRef(null), B = assignRefs(f, K), z = useCallback(()=>{
        var e, r;
        (null === (e = K.current) || void 0 === e ? void 0 : e.open) && (null === (r = K.current) || void 0 === r || r.close());
    }, []), A = useCallback((e)=>{
        L(false), isFunction(h) && e.target === K.current && h();
    }, [
        h
    ]);
    useEffect(()=>{
        K.current && (_ ? (L(true), P ? K.current.showModal() : K.current.show()) : z());
    }, [
        _,
        P,
        z
    ]), useEffect(()=>{
        let e = K.current, r = false, handleMouseDown = ()=>{
            r = false;
        }, handleMouseMove = ()=>{
            r = true;
        }, handleClick = (e)=>{
            P && e.target === K.current && !r && z();
        };
        if (e) return e.addEventListener('mousedown', handleMouseDown), e.addEventListener('mousemove', handleMouseMove), e.addEventListener('click', handleClick), e.addEventListener('close', A), ()=>{
            e.removeEventListener('mousedown', handleMouseDown), e.removeEventListener('mousemove', handleMouseMove), e.removeEventListener('click', handleClick), e.removeEventListener('close', A);
        };
    }, [
        z,
        A,
        P
    ]);
    let F = className('swan-dialog', 'swan-vanilla-ignore', {
        'swan-dialog-modeless': !P
    }, ...processStyleProps(x), x.className), G = _object_spread_props(_object_spread({}, removeCoreProps(x)), {
        className: F,
        'aria-modal': P ? 'true' : 'false'
    });
    return x['aria-label'] || x['aria-labelledby'] || !S || (G['aria-labelledby'] = S), jsx(ModalDialogContext.Provider, {
        value: {
            showDialog: C,
            variant: g,
            transitionState: I,
            handleModalCloseButton: z,
            titleId: S,
            setTitleId: N,
            bgc: k,
            backgroundColor: E,
            takeOver: null != j && j,
            isModal: null != P && P,
            bodyWidth: w || 'standard',
            footerPinned: R,
            setFooterPinned: W
        },
        children: jsx(O, {
            config: {
                inModal: true,
                portal: false,
                portalRef: q,
                dialogRef: K
            },
            children: jsx(RenderComp, {
                root: "dialog",
                forwardedRef: B,
                props: G,
                children: !M || _ ? D : null
            })
        })
    });
});

export { Dialog, h as DialogConfigContext, g as useInternalDialogConfigContext };
